/**
 * Правила никнейма (BS §11.3) — зеркало серверных: 5–32 символа латиницы, цифр и `_`,
 * первый — буква, последний — буква или цифра, без двух `_` подряд. Сервер публикует
 * те же значения в `GET /config → username`; здесь — запасные значения, разбор причин
 * для подсказок по мере ввода и сборка публичной ссылки `<origin>/@никнейм`.
 */
import type { Config } from '@shared/api/types'

export const USERNAME_MIN = 5
export const USERNAME_MAX = 32
export const USERNAME_SEARCH_MIN = 5

const USERNAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{3,30}[A-Za-z0-9]$/
const HANDLE_CHARS = /^[A-Za-z0-9_]*$/

export interface UsernameRules {
  min: number
  max: number
  searchMin: number
  pattern: RegExp
  linkBase: string | null
}

/** Правила из конфигурации сервера с запасными значениями контракта. */
export function usernameRules(config: Config | null | undefined): UsernameRules {
  const block = config?.username
  let pattern = USERNAME_PATTERN
  if (block?.pattern) {
    try {
      pattern = new RegExp(block.pattern)
    } catch {
      // Некорректное выражение в конфигурации не должно ломать ввод — остаётся встроенное.
    }
  }
  return {
    min: block?.min_length ?? USERNAME_MIN,
    max: block?.max_length ?? USERNAME_MAX,
    searchMin: block?.search_min_length ?? USERNAME_SEARCH_MIN,
    pattern,
    linkBase: block?.link_base ?? null,
  }
}

/** Убирает пробелы и ведущий `@`, который люди набирают по привычке. */
export function normalizeUsername(raw: string): string {
  return raw.trim().replace(/^@/, '')
}

/**
 * Причины, которые клиент различает сам — чтобы подсказка называла конкретную ошибку,
 * а не общее «неверный формат». Сервер отвечает грубее (`too_short`, `too_long`,
 * `invalid_format`, `reserved`, `taken`); их тексты — в той же таблице.
 */
export type UsernameProblem =
  | 'too_short'
  | 'too_long'
  | 'bad_chars'
  | 'first_not_letter'
  | 'ends_with_underscore'
  | 'double_underscore'
  | 'invalid_format'

export function usernameProblem(value: string, rules: UsernameRules): UsernameProblem | null {
  if (!HANDLE_CHARS.test(value)) return 'bad_chars'
  if (value.length < rules.min) return 'too_short'
  if (value.length > rules.max) return 'too_long'
  if (!/^[A-Za-z]/.test(value)) return 'first_not_letter'
  if (value.endsWith('_')) return 'ends_with_underscore'
  if (value.includes('__')) return 'double_underscore'
  if (!rules.pattern.test(value)) return 'invalid_format'
  return null
}

export const USERNAME_PROBLEM_TEXT: Record<UsernameProblem | 'reserved' | 'taken', string> = {
  too_short: `Минимум ${USERNAME_MIN} символов`,
  too_long: `Максимум ${USERNAME_MAX} символа`,
  bad_chars: 'Только латинские буквы, цифры и подчёркивание',
  first_not_letter: 'Первый символ — латинская буква',
  ends_with_underscore: 'Не может заканчиваться подчёркиванием',
  double_underscore: 'Два подчёркивания подряд нельзя',
  invalid_format: 'Недопустимый формат',
  reserved: 'Этот никнейм недоступен',
  taken: 'Этот никнейм уже занят',
}

/** Публичная ссылка на профиль: база из конфигурации, иначе собственный origin. */
export function profileLink(username: string, rules: UsernameRules): string {
  const base = rules.linkBase ?? (typeof location === 'undefined' ? '/' : `${location.origin}/`)
  return `${base.endsWith('/') ? base : `${base}/`}@${username}`
}

/** Ссылка без схемы — так её показывают рядом с полем, как в Telegram. */
export function displayLink(link: string): string {
  return link.replace(/^https?:\/\//, '')
}

/**
 * Запрос для глобального поиска: не короче `searchMin` символов алфавита никнейма
 * (без учёта ведущего `@`); иначе поиск по никнеймам не выполняется (BS §11.3).
 */
export function usernameSearchQuery(raw: string, rules: UsernameRules): string | null {
  const query = normalizeUsername(raw)
  if (query.length < rules.searchMin || query.length > rules.max || !HANDLE_CHARS.test(query)) return null
  return query
}

/** Текст для 429 `username_changes` (BS §20.1): пять успешных смен в сутки, дальше — ожидание. */
export function usernameRateLimitText(retryAfter: number | null | undefined): string {
  const base = 'Никнейм можно менять не чаще пяти раз в сутки'
  if (!retryAfter || retryAfter <= 0) return `${base}.`
  const hours = Math.floor(retryAfter / 3600)
  const minutes = Math.ceil((retryAfter % 3600) / 60)
  const wait =
    hours > 0
      ? `${hours} ч${minutes > 0 && minutes < 60 ? ` ${minutes} мин` : ''}`
      : `${Math.max(minutes, 1)} мин`
  return `${base}. Повторить можно через ${wait}.`
}
